/**
 * Kategorisiere Bands nach ihren teilgenommen Einträgen
 * @param {Array} bandData - Array von Bands mit attributes.teilgenommen
 * @returns {Object} Object mit Kategorien als Keys und Band-Arrays als Values
 */
export function categorizeBandsByParticipation(bandData) {
    const categories = {};

    bandData.forEach(bandRaw => {
        const band = bandRaw.attributes;
        const teilgenommen = band.teilgenommen || [];

        if (teilgenommen.length === 0) {
            // Bands ohne Teilnahmedaten
            if (!categories['Keine Angabe']) {
                categories['Keine Angabe'] = [];
            }
            categories['Keine Angabe'].push(bandRaw);
        } else {
            // Kategorisiere nach jedem teilgenommen-Eintrag
            teilgenommen.forEach(entry => {
                const category = entry.teilgenommen || 'Unbekannt';
                if (!categories[category]) {
                    categories[category] = [];
                }
                if (!categories[category].find(b => b.id === bandRaw.id)) {
                    categories[category].push(bandRaw);
                }
            });
        }
    });

    return categories;
}

/**
 * Sortiere kategorisierte Bands nach Jahren (neueste zuerst)
 * @param {Object} categories - Objekt mit kategorisierten Bands
 * @returns {Array} Array von [kategorie, bands] Paaren, sortiert nach Jahr
 */
export function sortCategoriesByYear(categories) {
    return Object.entries(categories).sort((a, b) => {
        // Extrahiere Jahre aus Kategorien (z.B. "2024", "AKKUSTIK2024")
        const yearA = parseInt(a[0].match(/\d{4}/)?.[0] || 0);
        const yearB = parseInt(b[0].match(/\d{4}/)?.[0] || 0);
        return yearB - yearA; // Neueste zuerst
    });
}

/**
 * Gruppiere Bands nach Jahr ihrer ersten Teilnahme
 * @param {Array} bandData - Array von Bands
 * @returns {Object} Object mit Jahren als Keys
 */
export function categorizeBandsByFirstYear(bandData) {
    const categories = {};

    bandData.forEach(bandRaw => {
        const band = bandRaw.attributes;
        const teilgenommen = band.teilgenommen || [];

        if (teilgenommen.length > 0) {
            // Finde das älteste Jahr
            const years = teilgenommen
                .map(entry => parseInt(entry.teilgenommen.match(/\d{4}/)?.[0]))
                .filter(year => !isNaN(year))
                .sort((a, b) => a - b);

            const firstYear = years[0] ? years[0].toString() : 'Unbekannt';
            if (!categories[firstYear]) {
                categories[firstYear] = [];
            }
            categories[firstYear].push(bandRaw);
        } else {
            if (!categories['Keine Angabe']) {
                categories['Keine Angabe'] = [];
            }
            categories['Keine Angabe'].push(bandRaw);
        }
    });

    return categories;
}

/**
 * Zähle, wie oft jede Band teilgenommen hat
 * @param {Array} bandData - Array von Bands
 * @returns {Object} Object mit Band-IDs und Teilnahmezahlen
 */
export function countBandParticipations(bandData) {
    const participationCount = {};

    bandData.forEach(bandRaw => {
        const band = bandRaw.attributes;
        const teilgenommen = band.teilgenommen || [];
        participationCount[bandRaw.id] = {
            name: band.name,
            count: teilgenommen.length,
            events: teilgenommen.map(e => e.teilgenommen)
        };
    });

    return participationCount;
}

/**
 * Konvertiere Kategoriename in anzeigefähigen Namen
 * @param {string} category - Kategoriename (z.B. "J2025", "AKKUSTIK2024")
 * @returns {string} Anzeigename (z.B. "Rock im Garten 2025")
 */
export function getCategoryDisplayName(category) {
    if (category === 'Keine Angabe' || category === 'Unbekannt') {
        return category;
    }

    // Extrahiere Jahr aus Kategorie
    const yearMatch = category.match(/\d{4}/);
    if (!yearMatch) {
        return category;
    }

    const year = yearMatch[0];

    // Bestimme Veranstaltungstyp
    if (category.startsWith('AKKUSTIK') || category.toLowerCase().includes('akustik')) {
        return `Rock im Garten Akustik ${year}`;
    }
    if (category.startsWith('J')) {
        return `Rock im Garten ${year}`;
    }

    if (category.startsWith('XMAS') || category.toLowerCase().includes('xmas')) {
        return `Rock im Garten Weihnachten ${year}`;
    }

    return category;
}
